// The founding member offer (see founding.js on the server): a "spots left" line for
// visitors, and the numbered badge for the first 50 members.
import { useEffect, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { api } from './api';
import { useI18n } from './i18n';
import { announce, useUnreadMessages } from './adminAlerts';

// Opens the Market Matrix server in Discord (the Founders Council is a category in it).
const DISCORD_SERVER_URL = 'https://discord.com/channels/1133017341010116680';

let request = null;
function loadSpots() {
  if (!request) {
    request = fetch('/api/founding').then((r) => (r.ok ? r.json() : null)).catch(() => null);
  }
  return request;
}

export function FoundingOffer({ className = '' }) {
  const { t } = useI18n();
  const [spots, setSpots] = useState(null);
  useEffect(() => {
    let alive = true;
    loadSpots().then((s) => alive && setSpots(s));
    return () => {
      alive = false;
    };
  }, []);
  if (!spots || !spots.left) return null;
  return (
    <div className={`founding-offer ${className}`}>
      <p className="founding-offer-title"><span className="founding-star" aria-hidden="true">★</span> {t('Become a Founding Member: the next {left} traders', { left: spots.left })}</p>
      <p>{t('Get 50% off any Market Matrix plan for as long as your account stays open, even if you cancel and come back, plus an exclusive numbered badge. You can also join the private Founders Council on our Discord to request features and vote on what we build next. Just create a free account: no payment needed.')}</p>
      <p className="founding-offer-core">{t('Your core journal stays free.')}</p>
      <p className="founding-offer-count">
        {spots.left === 1 ? t('1 founding spot left') : t('{left} founding spots left', { left: spots.left })}
      </p>
    </div>
  );
}

// The homepage's one-line version of the offer, under the hero.
export function FoundingStrip() {
  const { t, lang } = useI18n();
  const [spots, setSpots] = useState(null);
  useEffect(() => {
    let alive = true;
    loadSpots().then((s) => alive && setSpots(s));
    return () => {
      alive = false;
    };
  }, []);
  if (!spots || !spots.left) return null;
  return (
    <aside className="founding-strip" aria-label={t('Founding member offer')}>
      <p className="founding-strip-text">
        <strong><span className="founding-star" aria-hidden="true">★</span> {t('Founding member: 50% off any plan for life')}</strong>{' '}
        <span>{t('plus a numbered badge and a seat on the Founders Council in Discord. Just create a free account.')}</span>
      </p>
      <span className="founding-strip-count">{spots.left === 1 ? t('1 founding spot left') : t('{left} founding spots left', { left: spots.left })}</span>
      <Link to={lang === 'es' ? '/es/pricing' : '/pricing'} className="founding-strip-link">{t('Details')} &rarr;</Link>
    </aside>
  );
}

// The creator's gold badge. For an admin it opens the admin page and shows how many inbox
// messages are unread, with a ding and a desktop notification when a new one comes in.
function FounderBadge({ user }) {
  const { t } = useI18n();
  const [unread] = useUnreadMessages(Boolean(user.isAdmin), announce);
  const inner = (
    <>
      <span aria-hidden="true">★</span>
      <span className="founding-badge-text">{t('Founder')}</span>
      {unread > 0 && <span className="founder-unread" aria-label={`${unread} unread messages`}>{unread > 99 ? '99+' : unread}</span>}
    </>
  );
  if (!user.isAdmin) return <span className="founding-badge founder-badge" title={t('Founder of Market Matrix')}>{inner}</span>;
  return (
    <Link to="/admin" className="founding-badge founder-badge" title={unread ? `Admin · ${unread} unread` : 'Admin'}>
      {inner}
    </Link>
  );
}

export function FoundingBadge({ user }) {
  const { t } = useI18n();
  if (user && user.isCreator) return <FounderBadge user={user} />;
  if (!user || !user.foundingNumber) return null;
  const label = t('Founding member #{n}', { n: user.foundingNumber });
  return (
    <Link to="/app/settings#founders" className="founding-badge" title={t('See your founding member benefits')}>
      <span aria-hidden="true">★</span>
      <span className="founding-badge-text">{label}</span>
    </Link>
  );
}

// What came back from linking Discord (discord.js sends members to /app/settings?discord=...).
const DISCORD_RESULTS = {
  joined: ['success', "You're in. The Founders Council channels are now open in the Market Matrix Discord."],
  cancelled: ['', 'Discord linking was cancelled. You can try again anytime.'],
  failed: ['error', "We couldn't link your Discord account. Please try again, or contact us and we'll add you by hand."],
  unavailable: ['error', "The Founders Council link isn't switched on yet. Please try again later."],
  'not-founder': ['error', 'The Founders Council is for founding members.'],
};

export function FoundingCard({ user, onUserChange }) {
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const isFounder = Boolean(user && user.foundingNumber);
  // Opened from the badge or the Home card: bring the card into view.
  useEffect(() => {
    if (isFounder && (location.hash === '#founders' || searchParams.get('discord'))) {
      setTimeout(() => document.getElementById('founders')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
    }
  }, [isFounder, location.hash, searchParams]);
  if (!isFounder) return null;
  const result = DISCORD_RESULTS[searchParams.get('discord')];

  async function disconnect() {
    setBusy(true);
    setError('');
    try {
      onUserChange(await api('/discord/disconnect', { method: 'POST' }));
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="settings-card founding-card" id="founders">
      <p className="eyebrow">★ {t('Founding member #{n}', { n: user.foundingNumber })}</p>
      <h2>{t("You're one of our first 50 members")}</h2>
      <p>{t('The core journal stays free for good, and you get 50% off any Market Matrix plan, at whatever it costs, for as long as your account stays open (Pro is $9.50 a month instead of $19 today). Thank you for being here early.')}</p>
      <div className="founding-council">
        <h3>{t('Founders Council')}</h3>
        <p>{t('A private space on our Discord where founders suggest features, vote on priorities and talk directly with the creator. Optional: your badge and discount don’t depend on it.')}</p>
        {result && <p className={result[0] || 'hint'}>{t(result[1])}</p>}
        {error && <p className="error">{error}</p>}
        {user.discord ? (
          <div className="founding-council-actions">
            <span className="hint">{t('Connected as {name}', { name: user.discord.name || 'Discord' })}</span>
            <a href={DISCORD_SERVER_URL} target="_blank" rel="noopener noreferrer" className="cta-button">{t('Open the Founders Council')} &rarr;</a>
            <button type="button" className="link-button" onClick={disconnect} disabled={busy}>{t('Disconnect Discord')}</button>
          </div>
        ) : (
          <a href="/api/discord/connect" className="cta-button">{t('Join the Founders Council on Discord')} &rarr;</a>
        )}
      </div>
    </section>
  );
}

const HOME_CARD_KEY = 'mm-founding-home-closed';

// On Home, until the founder joins the council or closes it: what being a founder means.
export function FoundingHomeCard({ user }) {
  const { t } = useI18n();
  const [closed, setClosed] = useState(() => {
    try {
      return localStorage.getItem(`${HOME_CARD_KEY}-${user.id}`) === '1';
    } catch {
      return false;
    }
  });
  if (!user.foundingNumber || user.discord || closed) return null;

  function close() {
    setClosed(true);
    try {
      localStorage.setItem(`${HOME_CARD_KEY}-${user.id}`, '1');
    } catch {
      // Not remembered in private mode; it hides for this visit.
    }
  }

  return (
    <section className="founding-home">
      <button type="button" className="founding-home-close" onClick={close} aria-label={t('Close')}>&times;</button>
      <p className="founding-home-label">★ {t('Founding member #{n}', { n: user.foundingNumber })}</p>
      <h2>{t('Welcome, founder. Here’s what you get:')}</h2>
      <ul>
        <li>{t('50% off any plan, for as long as your account stays open')}</li>
        <li>{t('Your numbered Founding Member badge')}</li>
        <li>{t('A voice in what we build: the private Founders Council on Discord')}</li>
      </ul>
      <div className="founding-home-actions">
        <a href="/api/discord/connect" className="cta-button">{t('Join the Founders Council on Discord')} &rarr;</a>
        <Link to="/app/settings#founders" className="founding-home-more">{t('See your founding member benefits')}</Link>
      </div>
    </section>
  );
}
